import { Component, OnInit, Output, EventEmitter, Input, ViewChild, ElementRef, ChangeDetectorRef } from '@angular/core';
import { CurrencyPipe } from '@angular/common';
import { TaskDataService,  } from '../../../../shared/services/task-data.service';
import { IPaymentMethod, ITaskRequest, ITaskVoucherData, TaskRequest, TaskVoucherData } from '../../../../modules/tasks/model/types';
import { FileUploadComponent } from '../../../../shared/components/dynamic-form-widgets/file-upload/file-upload.component';

import notify from 'devextreme/ui/notify';
import validationEngine from "devextreme/ui/validation_engine";
import { TreasuryDataService } from '../../../../shared/services/treasury-data.service';

import { filterLoanData, getNotificationTimeout, sendNotificationMessage } from '../../../../types/resource';
import { LoggingService } from '../../../../framework/logging/logging.service';
import { SeverityLevel } from '../../../../framework/logging/severity-level.model';
import { BehaviorSubject, lastValueFrom } from 'rxjs';

import { ProfileDataService } from '../../../../shared/services/profile-data.service';
import { MessageService } from '../../../../shared/components/framework/services/message.service';
import { FormUtils } from '../../../../shared/components/framework/services/form-builder.service';
import { LoanDataBase } from '../../baseclass/loandata-base';


@Component({
  standalone: false,
  selector: 'app-voucher',
  templateUrl: './voucher.component.html',
  styleUrls: ['./voucher.component.css'],
  providers: [CurrencyPipe]
})
export class VoucherComponent extends LoanDataBase implements OnInit {
  @ViewChild('formElement') formElement!: ElementRef;

  private _fileUploadComponent?: FileUploadComponent;
  @ViewChild(FileUploadComponent)
  set fileUploadComponent(comp: FileUploadComponent | undefined) {
    this._fileUploadComponent = comp;
    if (comp && this.files.length > 0) {
      comp.restoreFiles(this.files);
    }
  }

  @Input() taskTemplateName: string;
  @Input() task?: ITaskRequest;

  @Output() onAddNewTaskEventEmitter: EventEmitter<any> = new EventEmitter();

  taskVoucherTypes: any[];
  isLoading: boolean = false;
  bankData: any[];
  voucherData:  ITaskVoucherData;
  billableToClientData: any[];
  businessDayCalendars: any[];
  files: File[] = [];
  directors: any[];
  secondDirectors: any[];
  paymentMethods: IPaymentMethod[];
  paymentTypeDisabled: boolean = true;
  paymentTypeDisabledBehaviourSubject$ = new BehaviorSubject<boolean>(true);
  paymentTypeDisabled$ = this.paymentTypeDisabledBehaviourSubject$.asObservable();
  today: Date;
  isEditable: boolean = false;
  fileSelectionText: string;
  formItemsBehaviourSubject$ = new BehaviorSubject<any[]>([]);
  formItems$ = this.formItemsBehaviourSubject$.asObservable();
  formItems: any[] = [];
  currentFormInstance: any = null;
  lastConfiguredRequestType: string | null = null;
  currencyData: any[] = [];
  sortedCurrencyData: any[] = [];
  currencyEditorOptions: any = {};
  amountEditorOptions: any = {};


  constructor(
    taskDataService: TaskDataService,
    private readonly treasuryDataService: TreasuryDataService,
    private readonly profileDataService: ProfileDataService,
    private readonly messageService: MessageService,
    private readonly loggingService: LoggingService,
    private readonly currencyPipe: CurrencyPipe,
    private readonly cdr: ChangeDetectorRef
  ) {
    super(taskDataService);
    this.onValidateSecondaryApprover = this.onValidateSecondaryApprover.bind(this);
    this.onRequestTypeSelectionChanged = this.onRequestTypeSelectionChanged.bind(this);
    this.onValidateFileAttachments = this.onValidateFileAttachments.bind(this);
    this.onDirectorChanged = this.onDirectorChanged.bind(this);
    this.onSecondDirectorChanged = this.onSecondDirectorChanged.bind(this);
    this.onVoucherAmountChanged = this.onVoucherAmountChanged.bind(this);
    this.onDebitBankChanged = this.onDebitBankChanged.bind(this);
    this.onTreasuryProvideFedRefNumbersChanged = this.onTreasuryProvideFedRefNumbersChanged.bind(this);
    this.onIsUrgentChanged = this.onIsUrgentChanged.bind(this);
    this.onPaymentMethodChanged = this.onPaymentMethodChanged.bind(this);

    this.paymentTypeDisabled$.subscribe((value: boolean) => {
      this.paymentTypeDisabled = value;
    });

    this.formItems$.subscribe((items: any[]) => {
      this.formItems = items;
    });

    this.loanData$.subscribe((data) => {
      this.loanData = data;
      if (this.currentFormInstance) {
        const loanEditor = this.currentFormInstance.getEditor('loanNumber');
        if (loanEditor) {
          loanEditor.option('items', data);
        }
      }
    });
  }

  onFileUpload(file: File)
  {
    this.files.push(file);
  }

  onFileRemove(file: File)
  {
    if(file != null && file != undefined)
    {
      this.files = this.files.filter(f => f !== file);
    }
  }

  onValidateVoucherAmount(e: any) : boolean
  {
    if(e.value == null || e.value <= 0)
      return false;

    return true;
  }

  onValidateFileAttachments(e: any) : boolean
  {
    if(this.files.length == 0 && !this.isEditable)
    {
      e.isValid = false;
      return false;
    }
    e.isValid = true;
    return true;
  }

  onValidateSecondaryApprover(e: any)
  {
    if(this.voucherData.voucherAmount >= 10000000 && e.value == null)
    {
      return false;
    }

    return true;
  }

  private sortCurrenciesWithPriority(currencies: any[]): any[] {
    const priorityCurrencies = ['USD', 'CAD', 'EUR', 'GBP'];

    // Filter priority currencies that exist in the data
    const priority = currencies.filter(c =>
      priorityCurrencies.includes(c.isoCode || c.code || c.name)
    ).sort((a, b) => {
      const aCode = a.isoCode || a.code || a.name;
      const bCode = b.isoCode || b.code || b.name;
      return priorityCurrencies.indexOf(aCode) - priorityCurrencies.indexOf(bCode);
    });

    // Get remaining currencies sorted alphabetically
    const remaining = currencies.filter(c =>
      !priorityCurrencies.includes(c.isoCode || c.code || c.name)
    ).sort((a, b) => {
      const aName = a.name || a.isoCode || a.code;
      const bName = b.name || b.isoCode || b.code;
      return aName.localeCompare(bName);
    });

    return [...priority, ...remaining];
  }

  getCurrencyFormat(currencyCode: string): string {
    // Use Angular's currency pipe to get proper symbol and formatting
    try {
      // Get the currency symbol using Angular's currency pipe
      const sampleAmount = 0;
      const formatted = this.currencyPipe.transform(sampleAmount, currencyCode, 'symbol-narrow', '1.0-2') || '';

      // Extract the symbol (everything before any digits)
      const symbolMatch = formatted.match(/^([^\d.,]*)/);
      const symbol = symbolMatch ? symbolMatch[1].trim() : currencyCode;

      // Return DevExtreme number format with the extracted symbol
      return symbol ? `${symbol} #,##0.##` : '#,##0.##';
    } catch (error) {
      // Fallback to currency code if pipe fails
      return `${currencyCode} #,##0.##`;
    }
  }

  private initializeEditorOptions() {
    // Initialize currency editor options
    this.currencyEditorOptions = {
      items: this.sortedCurrencyData,
      valueExpr: 'isoCode',
      displayExpr: 'name',
      required: true,
      searchMode: 'contains',
      searchEnabled: true,
      onValueChanged: (e: any) => {
        this.onCurrencyChanged(e.value);
      }
    };

    // Initialize amount editor options
    this.updateAmountEditorOptions();
  }

  private updateAmountEditorOptions() {
    const selectedCurrency = this.sortedCurrencyData?.find(c =>
      (c.isoCode || c.code) === this.voucherData?.currency
    );
    const currencyCode = selectedCurrency?.isoCode || selectedCurrency?.code || 'USD';
    const currencyFormat = this.getCurrencyFormat(currencyCode);

    this.amountEditorOptions = {
      required: true,
      format: currencyFormat,
      min: 0,
      defaultValue: 0,
      onValueChanged: this.onVoucherAmountChanged
    };
  }

  onCurrencyChanged(newCurrencyCode: string) {
    // Update the amount field format when currency changes
    this.updateAmountEditorOptions();
    this.cdr.detectChanges();
  }

  onDirectorChanged(e: any) {
    if (this.voucherData) {
      this.voucherData.director = e.value;
    }
    if (this.currentFormInstance) {
      this.currentFormInstance.itemOption('secondDirector', 'editorOptions', {
        items: this.secondDirectors.filter(d => d.name !== e.value),
        valueExpr: 'name',
        displayExpr: 'name',
        searchMode: 'contains',
        searchEnabled: true,
        onValueChanged: this.onSecondDirectorChanged,
      });
    }
  }

  onSecondDirectorChanged(e: any) {
    if (this.voucherData) {
      this.voucherData.secondDirector = e.value;
    }
    if (this.currentFormInstance) {
      this.currentFormInstance.itemOption('director', 'editorOptions', {
        items: this.directors.filter(d => d.name !== e.value),
        valueExpr: 'name',
        displayExpr: 'name',
        searchMode: 'contains',
        searchEnabled: true,
        onValueChanged: this.onDirectorChanged,
      });
    }
  }

  onVoucherAmountChanged(e: any) {
    if (this.voucherData) {
      this.voucherData.voucherAmount = e.value;
    }
    const currentRequestType = this.voucherData?.requestType;
    if (currentRequestType && this.lastConfiguredRequestType) {
      this.applyRequestTypeConfiguration(currentRequestType, true);
    }
  }

  onDebitBankChanged(e: any) {
    if (this.voucherData) {
      this.voucherData.debitBank = e.value;
    }
  }

  onTreasuryProvideFedRefNumbersChanged(e: any) {
    if (this.voucherData) {
      this.voucherData.treasuryProvideFedRefNumbers = e.value;
      if (!e.value) {
        this.voucherData.wireInformationForFedRefNumbers = '';
      }
    }
    const currentRequestType = this.voucherData?.requestType;
    if (currentRequestType && this.lastConfiguredRequestType) {
      this.applyRequestTypeConfiguration(currentRequestType, true);
    }
  }

  onIsUrgentChanged(e: any) {
    if (this.voucherData) {
      this.voucherData.isUrgent = e.value;
      if (e.value) {
        this.voucherData.voucherDueDate = this.today;
      }
    }
    const currentRequestType = this.voucherData?.requestType;
    if (currentRequestType && this.lastConfiguredRequestType) {
      this.applyRequestTypeConfiguration(currentRequestType, true);
    }
  }

  onPaymentMethodChanged(e: any) {
    if (this.voucherData) {
      this.voucherData.paymentMethod = e.value;
      const isCheckBased = e.value === 'Check' || e.value === 'E-Check';
      if (isCheckBased && this.voucherData.treasuryProvideFedRefNumbers) {
        this.voucherData.treasuryProvideFedRefNumbers = false;
        this.voucherData.wireInformationForFedRefNumbers = '';
      }
    }
    const currentRequestType = this.voucherData?.requestType;
    if (currentRequestType && this.lastConfiguredRequestType) {
      this.applyRequestTypeConfiguration(currentRequestType, true);
    }
  }

  removeLocationData(data: string): string
  {
    let json = JSON.parse(data);
    delete json['locations'];
    return JSON.stringify(json);
  }

  private getVoucherTemplate(requestType?: string)
  {
    const normalizedRequestType = requestType?.trim().toUpperCase();

    if (!normalizedRequestType || !this.taskVoucherTypes?.length) {
      return undefined;
    }

    return this.taskVoucherTypes.find((template) => template?.name?.trim().toUpperCase() === normalizedRequestType);
  }

  private getUsersForGroup(groups: any[], groupName: string): any[] {
    return groups.find((group) => group?.groupName === groupName || group?.GroupName === groupName)?.users ?? [];
  }

  private buildRequestTypeItem(): any {
    return {
      dataField: "requestType",
      editorType: "dxSelectBox",
      label: { text: "Request Type" },
      colSpan: 2,
      cssClass: "dx-widget",
      editorOptions: {
        items: this.taskVoucherTypes,
        valueExpr: "name",
        displayExpr: "name",
        disabled: this.isEditable,
        required: "true",
        searchMode: "contains",
        searchEnabled: true,
        onSelectionChanged: this.onRequestTypeSelectionChanged,
      },
      validationRules: [{ type: "required", message: "Request Type is required" }],
    };
  }

  private buildInitialFormItems(): any[] {
    return [this.buildRequestTypeItem()];
  }

  private buildDefaultFormItems(selectedRequestType: string): any[] {
    const normalizedRequestType = selectedRequestType.trim().toUpperCase();
    const paymentEnabled = normalizedRequestType === 'THIRD PARTY - TAX'
      || normalizedRequestType === 'THIRD PARTY - INSURANCE';
    const paymentDisabled = !paymentEnabled;

    if (paymentDisabled && this.voucherData) {
      this.voucherData.paymentMethod = 'Wire';
    }

    if (normalizedRequestType === 'DIRECTION LETTER' && this.lastConfiguredRequestType !== 'DIRECTION LETTER' && this.voucherData) {
      this.voucherData.isUrgent = true;
    }

    this.paymentTypeDisabledBehaviourSubject$.next(paymentDisabled);

    return [
      this.buildRequestTypeItem(),
      this.buildTopCheckboxGroup(true),
      ...(this.voucherData?.treasuryProvideFedRefNumbers ? [{
        dataField: "wireInformationForFedRefNumbers",
        editorType: "dxTextArea",
        label: { text: "Wire Information for Fed Refs" },
        colSpan: 2,
        editorOptions: {
          height: 120,
          placeholder: "Enter wire information for Fed Refs...",
        },
        validationRules: [{ type: "required", message: "Wire Information for Fed Ref #(s) is required" }],
      }] : []),
      {
        dataField: "debitBank",
        editorType: "dxSelectBox",
        label: { text: "Debit Bank Name" },
        colSpan: 2,
        cssClass: "dx-widget",
        editorOptions: {
          items: this.bankData,
          valueExpr: "name",
          displayExpr: "name",
          required: "true",
          searchMode: "contains",
          searchEnabled: true,
          acceptCustomValue: true,
          onCustomItemCreating: (e: any) => ({ name: e.text }),
          onValueChanged: this.onDebitBankChanged,
        },
        validationRules: [{ type: "required", message: "Debit Bank is required" }],
      },
      {
        itemType: "group",
        colCount: 4,
        colSpan: 2,
        items: [
          {
            labelMode: "static",
            dataField: "loanNumber",
            editorType: "dxSelectBox",
            colSpan: 3,
            cssClass: "dx-widget",
            label: { text: "Loan Number" },
            editorOptions: {
              items: this.loanData,
              valueExpr: "name",
              displayExpr: "name",
              required: "true",
              searchMode: "contains",
              searchEnabled: true,
              acceptCustomValue: true,
              onCustomItemCreating: (e: any) => ({ name: e.text }),
              itemTemplate: this.loanItemTemplate,
            },
            validationRules: [{ type: "required", message: "Loan Number is required" }],
          },
          {
            editorType: "dxCheckBox",
            label: { visible: false },
            cssClass: "loan-checkbox-center-align",
            editorOptions: {
              text: this.activeLoanCheckboxText,
              onValueChanged: this.onShowInactiveLoansChanged,
              value: false,
            },
          },
        ],
      },
      {
        dataField: "voucherAmount",
        editorType: "dxNumberBox",
        label: { text: "Voucher Amount" },
        colSpan: 2,
        editorOptions: this.amountEditorOptions,
        validationRules: [
          {
            type: "required",
            message: "Voucher Amount is required",
          },
          {
            type: "custom",
            message: "Voucher Amount must be greater than zero",
            validationCallback: (e: any) => this.onValidateVoucherAmount(e),
            reevaluate: true,
          },
        ],
      },
      {
        dataField: "currency",
        editorType: "dxSelectBox",
        label: { text: "Currency" },
        colSpan: 2,
        cssClass: "dx-widget",
        editorOptions: this.currencyEditorOptions,
        validationRules: [{ type: "required", message: "Currency is required" }],
      },
      {
        itemType: "group",
        colCount: 2,
        colSpan: 2,
        items: [
          {
            dataField: "paymentMethod",
            editorType: "dxRadioGroup",
            label: { text: "Payment Method" },
            editorOptions: {
              items: this.paymentMethods,
              valueExpr: "method",
              displayExpr: "text",
              layout: "horizontal",
              disabled: paymentDisabled,
              onValueChanged: this.onPaymentMethodChanged,
            },
          },
          {
            dataField: "isUrgent",
            editorType: "dxCheckBox",
            label: { visible: false },
            editorOptions: { text: "Rush Job?", value: this.voucherData?.isUrgent ?? false, onValueChanged: this.onIsUrgentChanged },
          },
        ],
      },
      {
        dataField: "voucherDueDate",
        editorType: "dxDateBox",
        label: { text: "Voucher Due Date" },
        colSpan: 2,
        editorOptions: {
          value: this.voucherData?.isUrgent ? this.today : (this.voucherData?.voucherDueDate ?? this.today),
          disabled: this.voucherData?.isUrgent ?? false,
        },
        validationRules: [{ type: "required", message: "Voucher Due Date is required" }],
      },
      ...this.buildDirectorItems(),
      {
        colSpan: 2,
        horizontalAlignment: "center",
        itemType: "simple",
        name: "fileUpload",
        label: { text: this.isEditable ? "Upload Files" : "Upload Files *" },
        template: "fileUploadTemplate",
      },
      {
        dataField: "fileUploadProxy",
        colSpan: 2,
        itemType: "simple",
        label: { visible: false },
        editorType: "dxTextBox",
        editorOptions: {
          visible: false,
          value: this.files.length.toString(),
        },
        validationRules: [
          {
            type: "custom",
            message: "At least one file must be attached",
            validationCallback: (e: any) => this.onValidateFileAttachments(e),
            reevaluate: true,
          },
        ],
      },
      {
        colSpan: 2,
        horizontalAlignment: "left",
        cssClass: "borrow-submit",
        editorType: "dxButton",
        editorOptions: { text: "Submit", useSubmitBehavior: true },
      },
    ];
  }

  private buildTopCheckboxGroup(includeTreasuryProvideFedRefNumbers: boolean): any {
    const isCheckBased = this.voucherData?.paymentMethod === 'Check' || this.voucherData?.paymentMethod === 'E-Check';
    const items: any[] = [
      {
        dataField: "feesPaidToMS",
        editorType: "dxCheckBox",
        label: { visible: false },
        editorOptions: { text: "Include Fees Paid to MS" },
      },
      {
        dataField: "billableToClient",
        editorType: "dxCheckBox",
        label: { visible: false },
        editorOptions: { text: "Billable to Client" },
      },
    ];

    if (includeTreasuryProvideFedRefNumbers) {
      items.push({
        dataField: "treasuryProvideFedRefNumbers",
        editorType: "dxCheckBox",
        label: { visible: false },
        editorOptions: {
          text: "Fed Refs Required",
          disabled: isCheckBased,
          value: isCheckBased ? false : (this.voucherData?.treasuryProvideFedRefNumbers ?? false),
          onValueChanged: this.onTreasuryProvideFedRefNumbersChanged,
        },
      });
    }

    return {
      itemType: "group",
      colCount: 3,
      colSpan: 2,
      items,
    };
  }

  private buildDirectorItems(): any[] {
    return [
      {
        dataField: "director",
        editorType: "dxSelectBox",
        label: { text: "Director" },
        colSpan: 2,
        cssClass: "dx-widget",
        editorOptions: {
          items: this.directors.filter(d => d.name !== this.voucherData?.secondDirector),
          valueExpr: "name",
          displayExpr: "name",
          searchMode: "contains",
          searchEnabled: true,
          onValueChanged: this.onDirectorChanged,
        },
        validationRules: [{ type: "required", message: "Director is required" }],
      },
      {
        dataField: "secondDirector",
        editorType: "dxSelectBox",
        label: { text: (this.voucherData?.voucherAmount ?? 0) >= 10000000 ? "Second Director *" : "Second Director" },
        colSpan: 2,
        cssClass: "dx-widget",
        editorOptions: {
          items: this.secondDirectors.filter(d => d.name !== this.voucherData?.director),
          valueExpr: "name",
          displayExpr: "name",
          searchMode: "contains",
          searchEnabled: true,
          onValueChanged: this.onSecondDirectorChanged,
        },
        validationRules: [
          {
            type: "custom",
            message: "Second Director is required when the amount is $10,000,000 or more",
            validationCallback: this.onValidateSecondaryApprover,
            reevaluate: true,
          },
        ],
      },
    ];
  }

  private buildIntracompanyFormItems(selectedRequestType: string): any[] {
    const defaultItems = this.buildDefaultFormItems(selectedRequestType);

    defaultItems[1] = this.buildTopCheckboxGroup(false);

    return defaultItems.filter(
      (item) => item?.dataField !== 'director' && item?.dataField !== 'secondDirector',
    );
  }

  private applyRequestTypeConfiguration(selectedRequestType?: string, forceRefresh: boolean = false): void {
    const normalizedRequestType = selectedRequestType?.trim().toUpperCase();

    if (!normalizedRequestType) {
      this.lastConfiguredRequestType = null;
      this.formItemsBehaviourSubject$.next(this.buildInitialFormItems());
      return;
    }

    if (!forceRefresh && this.lastConfiguredRequestType === normalizedRequestType && this.formItems.length > 0) {
      return;
    }

    let config: any[];
    switch (normalizedRequestType) {
      case 'INTRACOMPANY':
        config = this.buildIntracompanyFormItems(selectedRequestType!);
        break;
      default:
        config = this.buildDefaultFormItems(selectedRequestType!);
        break;
    }

    this.lastConfiguredRequestType = normalizedRequestType;
    this.formItemsBehaviourSubject$.next(config);
  }

  onRequestTypeSelectionChanged(e: any)
  {
    const selectedRequestType = (e?.selectedItem?.name ?? e?.value ?? this.voucherData?.requestType)?.trim();

    if (!selectedRequestType) {
      return;
    }

    this.voucherData.requestType = selectedRequestType;
    this.applyRequestTypeConfiguration(selectedRequestType);
  }

  onFormContentReady(e: any): void
  {
    this.currentFormInstance = e.component;
  }

  async onSubmit(e: any)
  {
    e.preventDefault();

    let validationResult = validationEngine.validateGroup("formGroup");

    if (validationResult.isValid)
    {
      try
      {
        let request: ITaskRequest = new TaskRequest();
        const selectedTemplate = this.getVoucherTemplate(this.voucherData.requestType);

        if (!selectedTemplate?.id) {
          throw new Error(`Unable to find a voucher template for request type "${this.voucherData.requestType}".`);
        }

        request.templateId = selectedTemplate.id;

        request.name = this.voucherData.requestType;
        request.referenceName = this.voucherData.loanNumber;
        request.referenceType = 'Facility';
        request.dueDate = this.voucherData.voucherDueDate;

        const voucherDataCopy = { ...this.voucherData };
        if (!voucherDataCopy.treasuryProvideFedRefNumbers) {
          delete (voucherDataCopy as any).wireInformationForFedRefNumbers;
        }
        const requestJsonData = this.appendDealNameToSubmission(voucherDataCopy);

        let formData: FormData = new FormData();
        formData.append('templateId', request.templateId);
        formData.append('locations', request.locations);
        formData.append('name', request.name);
        formData.append('referenceName', request.referenceName);
        formData.append('requestJsonData', JSON.stringify(requestJsonData));
        formData.append('referenceType', request.referenceType);
        formData.append('dueDate', new Date(request.dueDate).toDateString());

        if(this.isEditable)
          formData.append('parentId', this.task.parentId);

        this.files.forEach((f) => {formData.append('files', f, f.name)});

        this.isLoading = true;

        const response = await lastValueFrom(this.taskDataService.insertTaskAsFormData(formData));
        sendNotificationMessage(`${response[1].data[0].task.taskId} task created`, this.messageService);

        this.isLoading = false;

        this.formElement.nativeElement.reset();
        this.formElement.nativeElement.submit();
      }
      catch(err: any)
      {
        this.isLoading = false;
        this._fileUploadComponent?.restoreFiles(this.files);
        this.loggingService.logError(err, SeverityLevel.Error);
        notify(err.message, 'error', getNotificationTimeout());
      }
    }
  }



  override async ngOnInit() {

    try
    {
      this.isLoading = true;

      await super.ngOnInit();

      this.getPaymentMethods();

      const typeData = await lastValueFrom(this.taskDataService.getTaskTemplatesByType(this.taskTemplateName));
      this.taskVoucherTypes = typeData[1];

      // this.bankData = await lastValueFrom(this.treasuryDataService.getBanks());
      this.bankData = [
        { name: 'Bank of America' },
        { name: 'Flagstar Bank' },
        { name: 'Huntington National Bank' },
        { name: 'Idaho First Bank' },
        { name: 'Northeast Bank' },
        { name: 'PNC Bank N.A.' },
        { name: 'PNC PAID' },
        { name: 'Royal Bank of Canada, New York' },
        { name: 'State Street Bank and Trust Company' },
        { name: 'US Bank CAD' },
        { name: 'Wells Fargo Bank NA' },
        { name: 'Wells Fargo Bank, NA-WELLT414' }
      ];
      this.currencyData = [{ name: 'US Dollar', isoCode: 'USD' }, { name: 'Canadian Dollar', isoCode: 'CAD' }];
      this.sortedCurrencyData = this.sortCurrenciesWithPriority(this.currencyData);

      const directorsData = await lastValueFrom(this.profileDataService.getUsersByGroup(["Directors", "Second Directors"]));
      const directorGroups = directorsData[1] ?? [];
      this.directors = this.getUsersForGroup(directorGroups, "Directors");
      this.secondDirectors = this.getUsersForGroup(directorGroups, "Second Directors");

      this.today = new Date();

      this.isEditable = this.task != null ? true : false;

      if(this.isEditable)
      {
        this.voucherData = FormUtils.mapFromTask<TaskVoucherData>(this.task, TaskVoucherData);
        if (!this.voucherData.voucherDueDate && this.task?.taskData) {
          const legacyTaskData = JSON.parse(this.task.taskData);
          this.voucherData.voucherDueDate = legacyTaskData?.dueDate;
        }
        this.initializeEditorOptions();
        this.onRequestTypeSelectionChanged({ selectedItem: { name: this.voucherData.requestType } });
      }
      else
      {
        this.voucherData = new TaskVoucherData();
        this.voucherData.paymentMethod = 'Wire';

        // Set default currency to USD
        if (this.sortedCurrencyData && this.sortedCurrencyData.length > 0) {
          const usdCurrency = this.sortedCurrencyData.find(c =>
            (c.isoCode || c.code || c.name) === 'USD'
          );
          this.voucherData.currency = (usdCurrency?.isoCode || usdCurrency?.code) ||
                                      (this.sortedCurrencyData[0]?.isoCode || this.sortedCurrencyData[0]?.code);
        }
        this.initializeEditorOptions();
      }

      if (!this.voucherData.requestType) {
        this.applyRequestTypeConfiguration();
      }

      this.cdr.detectChanges();
      this.isLoading = false;
    }
    catch(err: any)
    {
      this.loggingService.logError(err, SeverityLevel.Error);
      this.isLoading = false;
      notify(err.message, 'error', getNotificationTimeout());
    }
  }

  getPaymentMethods()
  {
    this.paymentMethods = [
      { key: 1, text: 'Wire', method: 'Wire' },
      { key: 2, text: 'Check', method: 'Check' },
      { key: 2, text: 'E-Check', method: 'E-Check' }
    ];
  }


}
